import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import { FaCheck, FaDownload, FaExclamationTriangle, FaTimes, FaTrash } from 'react-icons/fa';
import { API_URL_All } from '../../api';
import { toast } from "react-toastify";
import * as general from '../../libs/general';
import { CheckingTicket } from '../tickets/Tickets';
import axiosInstance from '../../axiosInstance';
import ReconciliationPaymentConfirmModal, { buildPaymentSummary } from '../reconciliation/ReconciliationPaymentConfirmModal';

const formatSignedNumber = (value) => {
  const num = Number(value ?? 0);
  if (!num) return '0';
  return (num > 0 ? '+' : '') + general.formatNumber(num);
};

const buildChangedItemTitle = (item) => {
  const parts = [
    `Cước lúc tạo phiếu: ${general.formatNumber(item.fee)}`,
    `Cước hiện tại: ${general.formatNumber(item.currentFee)} (${formatSignedNumber(item.feeDiff)})`,
  ];
  if (Number(item.currentWeight ?? 0) !== Number(item.weight ?? 0)) {
    parts.push(`Cân lúc tạo phiếu: ${general.formatDecimal(item.weight) || 0} kg, hiện tại: ${general.formatDecimal(item.currentWeight) || 0} kg`);
  }
  if (Number(item.currentSize ?? 0) !== Number(item.size ?? 0)) {
    parts.push(`Khối lúc tạo phiếu: ${general.formatDecimal(item.size) || 0} m3, hiện tại: ${general.formatDecimal(item.currentSize) || 0} m3`);
  }
  return parts.join('\n');
};

const NormalReconciliationReview = ({ data, handleSearch, canEdit = false, canDelete = true, nestedModal = false, readOnly = false, onClose }) => {
  const allowEdit = readOnly ? false : canEdit;
  const allowDelete = readOnly ? false : canDelete;
  const [show, setShow] = useState(true);
  const [model, setModel] = useState({});
  const [errorMessage, setErrorMessage] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [loadingExport, setLoadingExport] = useState(false);
  const [loadingConfirm, setLoadingConfirm] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [removedItems, setRemovedItems] = useState([]);
  const [removingOrderId, setRemovingOrderId] = useState(null);
  const [showPaymentConfirm, setShowPaymentConfirm] = useState(false);

  useEffect(() => {
    retrieveModalData(data);
  }, [data]);

  const retrieveModalData = async (params) => {
    if (params.id) {
      try {
        var urlApi = `/api/invoices/getNormalInvoice?id=${params.id}`;
        const response = await axiosInstance.get(urlApi);
        var obj = response.data;
        if (obj.success === false) {
          toast.error(obj.message);
          return;
        }
        setModel(obj.data);
        setShow(true);
      } catch (error) {
        console.error(error);
        setModel({ error: error.message });
        toast.error(error.message);
      }
      return;
    }
    const textIds = params.listIds.join(';');
    setModel({});
    try {
      var urlApi = `/api/invoices/getNormalInvoiceReview`;
      var listIds = { codes: params.listIds.join(';') };
      const response = await axiosInstance.post(urlApi, listIds,
        { headers: { 'Content-Type': 'application/json' } });
      var obj = response.data;
      if (obj.success === false) {
        toast.error(obj.message);
        return;
      }
      setModel(obj.data);
      setShow(true);
    } catch (error) {
      console.error(error);
      setModel({ error: error.message });
      toast.error(error.message);
    }
  };

  const handleModalClose = (e) => {
    document.querySelectorAll('.show .error-msg').forEach((item) => {
      if (item.parentNode) {
        item.parentNode.removeChild(item);
      }
    });
    document.activeElement.blur();
    setShow(false);
    setRemovedItems([]);
    if (typeof onClose === 'function') {
      onClose();
    } else if (nestedModal && handleSearch) {
      handleSearch();
    }
  };

  const handleSubmitForm = async (e) => {
    e.preventDefault();
    var errorTag = e.currentTarget.querySelector('.error-msg');
    if (errorTag) return;

    setLoadingSubmit(true);
    try {
      var requestApi = `/api/invoices/saveNormalInvoice`;
      const response = await axiosInstance.post(requestApi, model);
      if (!response.data.success) {
        general.showFormError('.show .modal-body', response.data);
        console.log(response.data);
        return;
      }
      toast.success(response.data.message);
      if (handleSearch) handleSearch();
      await retrieveModalData({ id: response.data.data });
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoadingSubmit(false);
    }
  };

  const handleExportInvoice = async (param) => {
    setLoadingExport(true);
    try {
      var requestApi = `${API_URL_All}/api/invoices/exportNormalInvoice?id=${param.id}`;
      var fileName = `Doi_soat_thuong_${param.id}.xlsx`;
      if (!param.id) {
        fileName = `Doi_soat_thuong_preview.xlsx`;
        const avaiableIds = data.listIds.filter(id => !removedItems.includes(id));
        const codes = avaiableIds.join(';');
        requestApi = `${API_URL_All}/api/invoices/exportNormalInvoice?codes=${codes}`;
      }
      await general.downloadFile(requestApi, fileName);
    } finally {
      setLoadingExport(false);
    }
  };

  const handlePaymentConfirm = () => {
    if (!allowEdit) {
      toast.warning("Bạn không có quyền cập nhật đối soát.");
      return;
    }
    setShowPaymentConfirm(true);
  };

  const handlePaymentSuccess = () => {
    if (handleSearch) handleSearch();
    handleModalClose();
  };

  const handleRemoveOrder = async (orderId) => {
    setRemovingOrderId(orderId);
    try {
      const updatedRemovedItems = [...removedItems, orderId];
      setRemovedItems(updatedRemovedItems);

      const newListIds = data.listIds.filter(
        id => !updatedRemovedItems.includes(id)
      );

      const updatedData = { ...data, listIds: newListIds };
      await retrieveModalData(updatedData);
    } finally {
      setRemovingOrderId(null);
    }
  };

  const handleDeleteInvoice = (e) => {
    if (!allowDelete) return;
    general.openConfirm(async () => {
      setLoadingDelete(true);
      try {
        var requestApi = `/api/invoices/deleteNormalInvoice?id=${model.id}`;
        const response = await axiosInstance.get(requestApi);

        if (!response.data.success) {
          toast.error(response.data.message);
          return;
        }
        toast.success(response.data.message);
        if (handleSearch) handleSearch();
        handleModalClose();
      } catch (error) {
        console.error(error);
        toast.error(error.message);
      } finally {
        setLoadingDelete(false);
      }
    });
  };

  return (
    <>
    <Modal
      show={show}
      onHide={handleModalClose}
      size="fullscreen"
      dialogClassName="modal-popupfull checking-review-dialog"
      className={[
        "checking-review-modal",
        nestedModal ? "accounting-nested-modal" : "",
      ].filter(Boolean).join(" ")}
      backdropClassName={nestedModal ? "accounting-nested-modal-backdrop" : undefined}
      enforceFocus={!nestedModal}
    >
      <div className="modal-dialog11 modal-fullscreen11">
        <form id="order_editor_form" className="form_validate normal-form" autoComplete="off" onSubmit={handleSubmitForm}>
          <div className="modal-content">
            <div className="modal-header position-relative flex-between">
              {model.id && (<span className="record_id">ID: {model.id}</span>)}
              <div>
                <h5 className="modal-title">{model.id ? `Chi tiết phiếu đối soát thường` : 'Xem trước phiếu đối soát thường'}</h5>
                {model.error && (
                  <div className="error-msg-server">{model.error}</div>
                )}
              </div>
              <button
                type="button"
                className="btn-close"
                onClick={handleModalClose}
                aria-label="Đóng"
              ></button>
            </div>
            <div className="modal-body">

              <div>

                <div className="row mb-2">
                  <div className="col-md-6">
                    <div className="row">
                      <label className="form-label col-md-3">Khách hàng: </label>
                      <div className="col-md-9">
                        <b>
                          {model.customerCode}
                          {model.customerName ? ` - ${model.customerName}` : ""}
                        </b>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="row">
                      <label className="form-label col-md-3">Ngày đối soát: </label>
                      <div className="col-md-2">
                        <b>{model.createdTimeText || "—"}</b>
                      </div>
                      <div className="col-md-7">
                        <CheckingTicket checkingId={model.checkingId} checkingName={model.checkingName} />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="row mb-2">
                  <div className="col-md-6">
                    <div className="row">
                      <label className="form-label col-md-3">Tổng đơn: </label>
                      <div className="col-md-9">
                        <b>{general.formatNumber(model.orderCount)}</b>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="row">
                      <label className="form-label col-md-3">Tổng cân (kg): </label>
                      <div className="col-md-9">
                        <b>
                          {Number.isFinite(model.totalPackageUnitValue)
                            ? model.totalPackageUnitValue.toFixed(2)
                            : "0.00"}
                        </b>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="row mb-2">
                  <div className="col-md-6">
                    <div className="row">
                      <label className="form-label col-md-3">Tổng M3: </label>
                      <div className="col-md-9">
                        <b>
                          {Number.isFinite(model.totalPackageUnitM3)
                            ? model.totalPackageUnitM3.toFixed(2)
                            : "0.00"}
                        </b>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="row mb-2">
                  <div className="col-md-6">
                    <div className="row">
                      <label className="form-label col-md-3">Tổng cước: </label>
                      <div className="col-md-9">
                        <b>{general.formatNumber(model.fee)} VNĐ</b>
                      </div>
                    </div>
                  </div>
                  {model.changedOrderCount > 0 && (
                    <div className="col-12 order-last mt-2">
                      <div className="alert alert-warning py-2 mb-0 d-flex align-items-center gap-2">
                        <FaExclamationTriangle className="text-danger flex-shrink-0" aria-hidden />
                        <span>
                          Có <b>{model.changedOrderCount}</b> đơn bị sửa sau khi tạo phiếu làm lệch số tiền đối soát.
                          Cước theo đơn hiện tại: <b>{general.formatNumber(model.currentFee)} VNĐ</b>{' '}
                          (<b>{formatSignedNumber(model.feeDiff)}</b> so với tổng cước lúc tạo phiếu). Các dòng lệch được tô đỏ bên dưới.
                        </span>
                      </div>
                    </div>
                  )}
                  <div className="col-md-6">
                    <div className="row">
                      {model.id && (
                        <div className="col-md-12">
                          <div className="row">
                            <label className="form-label col-3">Nội dung: </label>
                            <div className="col-9">
                              <b>{model.name}</b>
                            </div>
                          </div>
                        </div>
                      )}
                      {!model.id && (
                        <div className="col-md-12">
                          <div className="row">
                            <label className="form-label col-form-label col-3"><span className="required1">Nội dung</span></label>
                            <div className="col-9">
                              <input
                                type="text"
                                className="form-control"
                                placeholder="Nội dung đối soát"
                                data-required1="Phải nhập mục này"
                                autoComplete="off"
                                value={model.name}
                                onChange={(e) => setModel({ ...model, name: e.target.value })}
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

              </div>

              <section className="p-1 ">
                <div className="table-responsive border table-normal table-sticky">
                  <table className="table table-striped border-normal table-bordered">
                    <thead className="table-primary head-01">
                      <tr>
                        <th className="sticky text-center" width="40px">STT</th>
                        <th className="text-center"><span>Ngày tạo</span></th>
                        <th><span>Line</span></th>
                        <th className="text-center">Mã vận đơn TQ</th>
                        <th className="text-center">Mã vận đơn VN</th>
                        <th className="text-start">Tên hàng</th>
                        <th className="text-center">Số kiện</th>
                        <th className="text-center"><span>Cân nặng (kg)</span></th>
                        <th className="text-center"><span>Thể tích (m3)</span></th>
                        <th className="text-center"><span>Phí bảo hiểm</span></th>
                        <th className="text-center"><span>Phụ phí</span></th>
                        <th className="text-start">Phương thức lấy hàng</th>
                        <th className="text-start">Ghi chú</th>
                        <th className="text-start">Lưu trình</th>
                        <th className="text-center"><span>Tổng cước</span></th>
                        {!model.id && (
                          <th></th>
                        )}
                      </tr>
                    </thead>
                    {model.items && model.items.map((item, index) => (
                      <tbody className="tbody-row" key={index}>
                        <tr>
                          <td className="sticky text-center" title={`orderId: ${item.orderId}`}>
                            {index + 1}
                          </td>
                          <td className="text-success text-center" title={item.orderDate ? general.formatTimeShort(item.orderDate) : ""}>
                            {general.formatDate(item.orderDate)}
                          </td>
                          <td className="text-success text-start" title={`orderId: ${item.orderId}`}>{item.lineName}</td>
                          <td className="text-success text-center">{item.cnCode}</td>
                          <td className="text-success text-center">{item.vnCode}</td>
                          <td className="text-success text-start text-truncate mw-101" title={item.productName}>{item.productName}</td>
                          <td className="text-success text-center">{item.packageCount}</td>
                          <td className="text-success text-center">{general.formatDecimal(item.weight)}</td>
                          <td className="text-success text-center">{general.formatDecimal(item.size)}</td>
                          <td className="text-success text-center">{general.formatNumber(item.feeInsurance)}</td>
                          <td className="text-success text-center">{general.formatNumber(item.feeSurcharge)}</td>
                          <td className="text-success text-start text-truncate mw-101" title={item.shippingMethod}>{item.shippingMethod}</td>
                          <td className="text-success text-start text-truncate mw-101" title={item.note}>{item.note}</td>
                          <td className="text-success text-start text-truncate mw-101" title={item.progressName}>{item.progressName}</td>
                          {item.amountChanged ? (
                            <td
                              className="text-danger text-center fw-bold table-warning"
                              title={buildChangedItemTitle(item)}
                            >
                              <FaExclamationTriangle className="me-1" size={12} aria-hidden />
                              {general.formatNumber(item.fee)}
                              <div className="small fw-normal">
                                Hiện tại: {general.formatNumber(item.currentFee)} ({formatSignedNumber(item.feeDiff)})
                              </div>
                            </td>
                          ) : (
                            <td className="text-success text-center" title={`Fee: ${general.formatNumber(item.fee)}`}>{general.formatNumber(item.fee)}</td>
                          )}
                          {!model.id && (
                            <td>
                              <button
                                type="button"
                                className="btn p-0 border-0 bg-transparent"
                                onClick={() => handleRemoveOrder(item.orderId)}
                                title="Xóa đơn hàng"
                                disabled={removingOrderId === item.orderId}
                              >
                                {removingOrderId === item.orderId ? (
                                  <span className="spinner-border spinner-border-sm text-danger" role="status" aria-hidden="true" />
                                ) : (
                                  <FaTrash className="text-danger" size={15} />
                                )}
                              </button>
                            </td>
                          )}
                        </tr>
                      </tbody>
                    ))}

                    <tbody>
                      {errorMessage === '' && loaded && model.items && model.items.length === 0 && (
                        <tr>
                          <td colSpan={model.id ? 15 : 16} className="text-center">Không tìm thấy dữ liệu</td>
                        </tr>
                      )}
                      {errorMessage !== '' && (
                        <tr>
                          <td colSpan={model.id ? 15 : 16} className="text-center">{errorMessage}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </section>

            </div>
            <div className="modal-footer text-end">
              {!model.id && (
                <div className="flex-between w-100">
                  <div>
                    <span
                      className={`btn-sm btn btn-primary me-2 d-inline-flex align-items-center gap-1${loadingExport ? " disabled" : ""}`}
                      onClick={loadingExport ? undefined : () => handleExportInvoice({ codes: model.codes })}
                    >
                      {loadingExport ? (
                        <>
                          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                          Đang xuất...
                        </>
                      ) : (
                        <>
                          <FaDownload size={12} /> Xuất excel nháp
                        </>
                      )}
                    </span>
                  </div>
                  <div>
                    <button
                      type="submit"
                      className="btn-sm btn btn-primary d-inline-flex align-items-center gap-1"
                      disabled={loadingSubmit}
                    >
                      {loadingSubmit ? (
                        <>
                          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                          Đang xử lý...
                        </>
                      ) : (
                        <>
                          <FaCheck size={12} /> Xác nhận đối soát
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
              {model.id && (
                <div className="flex-between w-100">
                  <div className="d-flex align-items-center">
                    <span
                      className={`btn-sm btn btn-primary me-2 d-inline-flex align-items-center gap-1${loadingExport ? " disabled" : ""}`}
                      onClick={loadingExport ? undefined : () => handleExportInvoice({ id: model.id })}
                    >
                      {loadingExport ? (
                        <>
                          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                          Đang xuất...
                        </>
                      ) : (
                        <>
                          <FaDownload size={12} /> Xuất excel
                        </>
                      )}
                    </span>
                    {allowDelete && model.isAdmin && (
                      <button
                        type="button"
                        className="btn-small d-inline-flex align-items-center gap-1"
                        disabled={loadingDelete}
                        onClick={() => handleDeleteInvoice()}
                      >
                        {loadingDelete ? (
                          <>
                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                            Đang xóa...
                          </>
                        ) : (
                          <>
                            <FaTrash size={12} /> Xóa
                          </>
                        )}
                      </button>
                    )}
                  </div>
                  <div>
                    {!readOnly && model.checkingId === 14 && allowEdit && (
                      <span
                        className={`btn-sm btn btn-primary me-2 d-inline-flex align-items-center gap-1${loadingConfirm ? " disabled" : ""}`}
                        onClick={loadingConfirm ? undefined : () => handlePaymentConfirm()}
                      >
                        {loadingConfirm ? (
                          <>
                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                            Đang xử lý...
                          </>
                        ) : (
                          <>
                            <FaCheck size={12} /> Xác nhận thanh toán
                          </>
                        )}
                      </span>
                    )}
                    <button onClick={() => handleModalClose()} type="button" className="btn-sm btn btn-secondary d-inline-flex align-items-center gap-1">
                      <FaTimes size={12} /> Đóng
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </form>
      </div>
    </Modal>
    {!readOnly && (
      <ReconciliationPaymentConfirmModal
        show={showPaymentConfirm}
        onHide={() => setShowPaymentConfirm(false)}
        onSuccess={handlePaymentSuccess}
        completeApi="/api/invoices/completeNormalInvoice"
        payloadBase={model}
        summary={buildPaymentSummary(model, "customer")}
        accountLabel="Tài khoản nhận tiền"
      />
    )}
    </>
  );
};

export default NormalReconciliationReview;
